import { isEmpty } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoItemResult from '../../../components/results/NoItemResult';
import GroupedColumnChart from './Charts/GroupedColumnChart';
import { useDashboardContext } from './DashboardContext';
import usePaymentDepositReceivedData from './data/usePaymentDepositReceivedData';
import { FullScreenWrapper } from './shared/FullScreenWrapper';
import { type ChartDataProps, type Datum } from './shared/typings';
import useLegendSelectedConfig from './shared/useLegendSelectedConfig';
import useSeriesColors from './shared/useSeriesColors';
import useSeriesFields from './shared/useSeriesField';

export const DepositPaymentsPerDayViewID = 'depositPaymentsPerDay';

const DepositPaymentsPerDayContent = ({ data, t, noData }: ChartDataProps) => {
    const { currentMonth, lastMonth } = useSeriesFields();
    const legendSelected = useLegendSelectedConfig({ currentMonth, lastMonth });
    const color = useSeriesColors();

    if (noData) {
        return <NoItemResult subTitle={t('dashboard:noData.message')} />;
    }

    return (
        <GroupedColumnChart
            {...t('dashboard:depositPaymentsPerDay.chart', { returnObjects: true })}
            color={color}
            data={data}
            visible={legendSelected}
            xField="day"
            yField="amount"
        />
    );
};

const DepositPaymentsPerDay = () => {
    const { t } = useTranslation(['dashboard']);
    const { isLoading, error, generateData } = usePaymentDepositReceivedData();
    const [data, setData] = useState<Datum | null>(null);

    useEffect(() => {
        generateData(result => setData(result));
    }, [generateData, setData]);

    const noData = useMemo(() => (data ? Object.values(data).every(isEmpty) : true), [data]);

    const { setVisible } = useDashboardContext();

    useEffect(() => {
        setVisible({ [DepositPaymentsPerDayViewID]: !error });
    }, [error, setVisible]);

    if (error) {
        return null;
    }

    return (
        <FullScreenWrapper
            disabledFullScreen={noData}
            isLoading={isLoading}
            title={t('dashboard:depositPaymentsPerDay.title')}
        >
            <DepositPaymentsPerDayContent data={data} noData={noData} t={t} />
        </FullScreenWrapper>
    );
};

export default DepositPaymentsPerDay;
